/**
 * The vocabulary a Voice AI channel's handoff shares with Engagement.
 *
 * Deliberately a LEAF module with no imports at all. `build-voice-ai-config`
 * needs these symbols and is kept free of `api-services`: this package's vitest
 * aliases the bare `@pypestream/api-services` specifier to a stub but lets
 * SUBPATHS resolve to the real files, so any module that reaches
 * `api-services/urql` has to be mocked by every test that touches it. Reaching
 * these constants through `contact-center-voice` (which reads a connection, and
 * so does pull that chain in) would make the payload builder untestable in
 * isolation — the very thing its own header comment exists to prevent.
 */

/**
 * The `callType` that routes a voice handoff into Contact Center rather than to
 * a phone number or SIP URI.
 *
 * Matched as an exact string by Engagement's `HandoffOptionsResolver`
 * (`@voice_inbox_call_type "voice_inbox"`), on a channel routing rule
 * (`voice_handoff_options_b64[skill].type`) and on a solution-level
 * `extra_data.handoff_type` override alike.
 */
export const VOICE_INBOX_HANDOFF_TYPE = 'voice_inbox';

/**
 * Where a skill's escalation goes. `phone_number` and `sip` carry a
 * destination; `voice_inbox` carries none — the destination IS the Contact
 * Center inbox, which Engagement resolves from the channel's integration info.
 */
export type VoiceHandoffType =
  | 'phone_number'
  | 'sip'
  | typeof VOICE_INBOX_HANDOFF_TYPE;

/**
 * The decoded `metadata_integration_info_b64` for a Contact Center channel.
 *
 * `integrator` names the platform the blob belongs to, so the blob is
 * self-describing rather than inferred from which keys happen to be present.
 * Engagement does not require it — `id_value/3` reads the two ids out of
 * whatever the blob holds — but both Engagement readers that branch on it
 * (`maybe_start_session_enrichment`, `genesys_handoff_configured?`) are
 * Genesys-only and fall through for any other value, so carrying it costs
 * nothing and makes their "skipping non-Genesys integrator" log name the
 * platform instead of printing nil.
 *
 * Typed as `string` rather than the canonical literal to keep this module a
 * true leaf: the one spelling lives in `platform-catalog`, is stamped by
 * `toContactCenterIntegrationInfo`, and is enforced by the guards in
 * `get-channels-from-pypes`.
 *
 * snake_case ids because that is the spelling Engagement looks up, and
 * `inbox_id` rather than `voice_inbox_id` because Engagement accepts either
 * (`["voice_inbox_id", "inbox_id"]`) while `inbox_id` is what the connection
 * already stores in `integration_headers` — so the two sides agree on one name.
 */
export interface ContactCenterIntegrationInfo {
  integrator: string;
  account_id: string;
  inbox_id: string;
}
